import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { headerOffset } from "components/basic/header";
import { DEFAULT_PROFILE_TAB_HEIGHT } from "components/basic/tabs";
import { useUpdateAccountMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { formatPhoneNumber } from "pages/formTools";
import {
  notificationEmailChoices,
  selectedNotificationEmail,
} from "pages/userAuthentication/signUp/helpers/notificationEmails";
import { DashboardProfileData } from "../..";
import { ProfileEditButton } from "../../components/ProfileEditButton";
import DeleteAccountButton from "../../components/DeleteAccountButton";
import { useVendorFieldSave } from "../../helpers/useVendorFieldSave";
import SignatorySection from "./components/SignatorySection";

interface PrimaryContactProfileDashboardProps {
  /** Where the profile tabs live: "profile", or "application" before approval. */
  basePath?: string;
  dashboardProfileData: DashboardProfileData;
  onAccountChange: (name: string, value: string | boolean) => void;
  onVendorChange: (name: string, value: string | boolean) => void;
}

/** A vendor's "Personal" tab: who the business's primary contact is. */
const PrimaryContactProfileDashboard: React.FC<
  PrimaryContactProfileDashboardProps
> = ({
  basePath = "profile",
  dashboardProfileData,
  onAccountChange,
  onVendorChange,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("profile");
  const { account, vendor } = dashboardProfileData;
  const [, updateAccount] = useUpdateAccountMutation();
  const saveVendor = useVendorFieldSave();
  const [signatoryIncomplete, setSignatoryIncomplete] = useState(false);

  const handlePhotoSave = async (
    updates: Record<string, string | boolean | number>,
  ) => {
    const response = await updateAccount({ options: updates });
    const error =
      response.data?.updateAccount.errors?.[0].message ??
      response.error?.message;
    showSnack(error ?? t("saved"), error ? "error" : "success");
    return !error;
  };

  const handleSignatorySave = async (
    updates: Record<string, string | boolean>,
  ) => {
    const saved = await saveVendor(updates);
    if (saved) {
      Object.entries(updates).forEach(([name, value]) =>
        onVendorChange(name, value),
      );
    }
    return saved;
  };

  const handleNext = () => {
    if (signatoryIncomplete) {
      showSnack(t("signatory.incomplete"), "error");
      return;
    }
    navigate(`${basePath}/business`);
  };

  const handleIncompleteChange = useCallback(
    (incomplete: boolean) => setSignatoryIncomplete(incomplete),
    [],
  );

  if (!vendor) {
    return null;
  }

  const emailChoices = notificationEmailChoices(account.email, vendor);

  return (
    <div
      style={{
        display: "flex",
        overflow: "auto",
        scrollbarWidth: "thin",
        width: "100%",
        height: `calc(100vh - ${headerOffset(DEFAULT_PROFILE_TAB_HEIGHT + 1)})`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing(1.8),
          // Stretches to the parent's fixed viewport height, so the padding
          // has to sit inside it or it forces a scrollbar.
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "560px",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          <ProfileEditButton
            label={t("fields.profile_photo")}
            name="profilePhoto"
            input={account.profilePhotoUrl || ""}
            onChange={onAccountChange}
            onSave={handlePhotoSave}
            type="photo"
          />

          <ProfileEditButton
            label={t("fields.primary_contact")}
            name="primaryContact"
            input={[
              {
                label: t("fields.first_name"),
                name: "primaryContactFirstName",
                value: vendor.primaryContactFirstName,
              },
              {
                label: t("fields.last_name"),
                name: "primaryContactLastName",
                value: vendor.primaryContactLastName,
              },
              {
                label: t("fields.position"),
                name: "primaryContactPosition",
                value: vendor.primaryContactPosition,
              },
              {
                label: t("fields.email"),
                name: "primaryContactEmail",
                value: vendor.primaryContactEmail,
              },
              {
                label: t("fields.phone_number"),
                name: "primaryContactPhone",
                value: vendor.primaryContactPhone,
                mask: formatPhoneNumber,
              },
            ]}
            onChange={onVendorChange}
            onSave={saveVendor}
            type="list"
          />

          <SignatorySection
            vendor={vendor}
            onSave={handleSignatorySave}
            onIncompleteChange={handleIncompleteChange}
          />

          {emailChoices.length > 1 ? (
            <ProfileEditButton
              label={t("fields.preferred_email")}
              name="notificationEmail"
              input={{
                selectedValue: selectedNotificationEmail(account.email, vendor),
                optionList: emailChoices.map((email, index) => ({
                  id: index + 1,
                  label: email,
                })),
              }}
              onChange={onVendorChange}
              onSave={saveVendor}
              type="dropdown"
              description={t("fields.preferred_email_hint")}
            />
          ) : (
            <Typography
              variant="body2"
              align="center"
              style={{ opacity: 0.7, padding: theme.spacing(1) }}
            >
              {t("fields.notifications_to", { email: account.email })}
            </Typography>
          )}

          <DeleteAccountButton />
        </div>

        <div style={{ paddingTop: theme.spacing(2) }}>
          <Button
            style={{
              color: theme.palette.common.white,
              fontSize: "1.2em",
              fontWeight: "bold",
              padding: `0 ${theme.spacing(10)}`,
            }}
            variant="contained"
            color="warning"
            onClick={handleNext}
            size="large"
          >
            {t("next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrimaryContactProfileDashboard;
